/**
 * The code is based on 'ng2-file-upload' library (https://github.com/valor-software/ng2-file-upload)
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Dmitriy Shekhovtsov <valorkin@gmail.com>
 * Copyright (c) 2015 Valor Software
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { EventEmitter } from "@angular/core";
import { FileLikeObject } from "./file-like-object";
import { FileItem } from "./file-item";
import { FileType } from "./file-type";

function isFile(value: unknown): boolean {
    return value instanceof File;
}

export interface Headers {
    name: string;
    value: string;
}

export interface ParsedResponseHeaders {
    [headerFieldName: string]: string;
}

export interface FilterFunction {
    name: string;
    fn: (item: FileLikeObject, options?: FileUploaderOptions) => boolean;
}

/**
 * File uploader options.
 *
 * Parameters:
 * 1. `url` URL of File Uploader's route
 * 2. `authToken` Auth token that will be applied as 'Authorization' header during file send.
 * 3. `disableMultipart` If `true`, disable using a multipart form for file upload and instead stream the file.
 *     Some APIs (e.g. Amazon S3) may expect the file to be streamed rather than sent via a form.
 *     Defaults to `false`.
 * 4. `itemAlias` item alias (form name redefinition).
 * 5. `formatDataFunction` Function to modify the request body. 'DisableMultipart' must be `true` for this function to be called.
 * 6. `formatDataFunctionIsAsync` Informs if the function sent in `formatDataFunction` is asynchronous. Defaults to `false`.
 * 7. `parametersBeforeFiles` States if additional parameters should be appended before or after the file. Defaults to `false`.
 */
export interface FileUploaderOptions {
    url: string;
    additionalParameter?: { [key: string]: string };
    allowedMimeType?: string[];
    allowedFileType?: string[];
    authToken?: string;
    authTokenHeader?: string;
    autoUpload?: boolean;
    disableMultipart?: boolean;
    filters?: FilterFunction[];
    formatDataFunction?: Function;
    formatDataFunctionIsAsync?: boolean;
    headers?: Headers[];
    itemAlias?: string;
    method?: string;
    maxFileSize?: number;
    parametersBeforeFiles?: boolean;
    queueLimit?: number;
    removeAfterUpload?: boolean;
}

/**
 * File uploader class
 */
export class FileUploader {
    authToken?: string | undefined;
    isUploading = false;
    queue: FileItem[] = [];
    progress = 0;
    _nextIndex = 0;
    autoUpload: boolean | undefined = false;
    authTokenHeader?: string | undefined;
    response: EventEmitter<string>;

    options: FileUploaderOptions = {
        autoUpload: false,
        filters: [],
        removeAfterUpload: false,
        disableMultipart: false,
        formatDataFunction: (item: FileItem) => item._file,
        formatDataFunctionIsAsync: false,
        url: ""
    };

    protected _failFilterIndex?: number;

    constructor(options: FileUploaderOptions) {
        this.setOptions(options);
        this.response = new EventEmitter<string>();
    }

    setOptions(options: FileUploaderOptions): void {
        this.options = Object.assign(this.options, options);

        this.authToken = this.options.authToken;
        this.authTokenHeader = this.options.authTokenHeader || "Authorization";
        this.autoUpload = this.options.autoUpload;
        this.options.filters?.unshift({ name: "queueLimit", fn: this._queueLimitFilter });

        if (this.options.maxFileSize) {
            this.options.filters?.unshift({ name: "fileSize", fn: this._fileSizeFilter });
        }

        if (this.options.allowedFileType) {
            this.options.filters?.unshift({ name: "fileType", fn: this._fileTypeFilter });
        }

        if (this.options.allowedMimeType) {
            this.options.filters?.unshift({ name: "mimeType", fn: this._mimeTypeFilter });
        }

        for (const file of this.queue) {
            file.url = this.options.url;
        }
    }

    addToQueue(
        fileList: FileList | File[],
        _options?: FileUploaderOptions,
        filters?: [] | string
    ): void {
        let options = _options;
        const files = Array.from(fileList || []);
        const arrayOfFilters = this._getFilters(filters);
        const count = this.queue.length;
        const addedFileItems: FileItem[] = [];
        files.forEach((some: File) => {
            if (!options) {
                options = this.options;
            }
            const temp = new FileLikeObject(some);
            if (this._isValidFile(temp, arrayOfFilters, options)) {
                const fileItem = new FileItem(this, some, options);
                addedFileItems.push(fileItem);
                this.queue.push(fileItem);
                this._onAfterAddingFile(fileItem);
            } else if (typeof this._failFilterIndex === "number" && this._failFilterIndex >= 0) {
                const filter = arrayOfFilters[this._failFilterIndex];
                this._onWhenAddingFileFailed(temp, filter, options);
            }
        });
        if (this.queue.length !== count) {
            this._onAfterAddingAll(addedFileItems);
            this.progress = this._getTotalProgress();
        }
        this._render();
        if (this.options.autoUpload) {
            this.uploadAll();
        }
    }

    removeFromQueue(value: FileItem): void {
        const index = this.getIndexOfItem(value);
        const item = this.queue[index];
        if (item.isUploading) {
            item.cancel();
        }
        this.queue.splice(index, 1);
        this.progress = this._getTotalProgress();
    }

    clearQueue(): void {
        while (this.queue.length) {
            this.queue[0].remove();
        }
        this.progress = 0;
    }

    uploadItem(value: FileItem): void {
        const index = this.getIndexOfItem(value);
        const item = this.queue[index];
        item._prepareToUploading();
        if (this.isUploading) {
            return;
        }
        this.isUploading = true;
        this._xhrTransport(item);
    }

    cancelItem(value: FileItem): void {
        const index = this.getIndexOfItem(value);
        const item = this.queue[index];
        if (item && item.isUploading) {
            item._xhr?.abort();
        }
    }

    uploadAll(): void {
        const items = this.getNotUploadedItems().filter((item: FileItem) => !item.isUploading);
        if (!items.length) {
            return;
        }
        items.map((item: FileItem) => item._prepareToUploading());
        items[0].upload();
    }

    cancelAll(): void {
        const items = this.getNotUploadedItems();
        items.map((item: FileItem) => item.cancel());
    }

    isFile(value: unknown): boolean {
        return isFile(value);
    }

    isFileLikeObject(value: unknown): boolean {
        return value instanceof FileLikeObject;
    }

    getIndexOfItem(value: number | FileItem): number {
        return typeof value === "number" ? value : this.queue.indexOf(value);
    }

    getNotUploadedItems(): FileItem[] {
        return this.queue.filter((item: FileItem) => !item.isUploaded);
    }

    getReadyItems(): FileItem[] {
        return this.queue
            .filter((item: FileItem) => item.isReady && !item.isUploading)
            .sort((item1, item2) => (item1.index ?? 0) - (item2.index ?? 0));
    }

    onAfterAddingAll(fileItems: FileItem[]): { fileItems: FileItem[] } {
        return { fileItems };
    }

    onBuildItemForm(fileItem: FileItem, form: FormData): { fileItem: FileItem; form: FormData } {
        return { fileItem, form };
    }

    onAfterAddingFile(fileItem: FileItem): { fileItem: FileItem } {
        return { fileItem };
    }

    onWhenAddingFileFailed(
        item: FileLikeObject,
        filter: FilterFunction,
        options: unknown
    ): { item: FileLikeObject; filter: FilterFunction; options: unknown } {
        return { item, filter, options };
    }

    onBeforeUploadItem(fileItem: FileItem): { fileItem: FileItem } {
        return { fileItem };
    }

    onProgressItem(fileItem: FileItem, progress: number): { fileItem: FileItem; progress: number } {
        return { fileItem, progress };
    }

    onProgressAll(progress: number): { progress: number } {
        return { progress };
    }

    onSuccessItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { item: FileItem; response: string; status: number; headers: ParsedResponseHeaders } {
        return { item, response, status, headers };
    }

    onErrorItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { item: FileItem; response: string; status: number; headers: ParsedResponseHeaders } {
        return { item, response, status, headers };
    }

    onCancelItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { item: FileItem; response: string; status: number; headers: ParsedResponseHeaders } {
        return { item, response, status, headers };
    }

    onCompleteItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): { item: FileItem; response: string; status: number; headers: ParsedResponseHeaders } {
        return { item, response, status, headers };
    }

    onCompleteAll(): undefined {
        return undefined;
    }

    _mimeTypeFilter(item: FileLikeObject): boolean {
        return !(item?.type && this.options.allowedMimeType?.indexOf(item.type) === -1);
    }

    _fileSizeFilter(item: FileLikeObject): boolean {
        return !(this.options.maxFileSize && item.size > this.options.maxFileSize);
    }

    _fileTypeFilter(item: FileLikeObject): boolean {
        return !(this.options.allowedFileType?.indexOf(FileType.getMimeClass(item)) === -1);
    }

    _onErrorItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): void {
        item._onError(response, status, headers);
        this.onErrorItem(item, response, status, headers);
    }

    _onCompleteItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): void {
        item._onComplete(response, status, headers);
        this.onCompleteItem(item, response, status, headers);
        const nextItem = this.getReadyItems()[0];
        this.isUploading = false;
        if (nextItem) {
            nextItem.upload();
            return;
        }
        this.onCompleteAll();
        this.progress = this._getTotalProgress();
        this._render();
    }

    protected _xhrTransport(item: FileItem): void {
        const xhr = (item._xhr = new XMLHttpRequest());
        let sendable!: FormData;
        this._onBeforeUploadItem(item);

        if (typeof item._file.size !== "number") {
            throw new TypeError("The file specified is no longer valid");
        }
        if (!this.options.disableMultipart) {
            sendable = new FormData();
            this._onBuildItemForm(item, sendable);
            const appendFile = (): void =>
                sendable.append(item.alias ?? "", item._file, item.file.name);
            if (!this.options.parametersBeforeFiles) {
                appendFile();
            }

            // For AWS, Additional Parameters must come BEFORE Files
            if (this.options.additionalParameter !== undefined) {
                Object.keys(this.options.additionalParameter).forEach((key: string) => {
                    let paramVal = this.options.additionalParameter![key];
                    // Allow an additional parameter to include the filename
                    if (
                        typeof paramVal === "string" &&
                        paramVal.indexOf("{{file_name}}") >= 0 &&
                        item.file?.name
                    ) {
                        paramVal = paramVal.replace("{{file_name}}", item.file.name);
                    }
                    sendable.append(key, paramVal);
                });
            }

            if (appendFile && this.options.parametersBeforeFiles) {
                appendFile();
            }
        } else if (this.options.formatDataFunction) {
            sendable = this.options.formatDataFunction(item);
        }

        xhr.upload.onprogress = (event: ProgressEvent) => {
            const progress = Math.round(
                event.lengthComputable ? (event.loaded * 100) / event.total : 0
            );
            this._onProgressItem(item, progress);
        };
        xhr.onload = () => {
            const headers = this._parseHeaders(xhr.getAllResponseHeaders());
            const response = this._transformResponse(xhr.response, headers);
            if (this._isSuccessCode(xhr.status)) {
                this._onSuccessItem(item, response, xhr.status, headers);
            } else {
                this._onErrorItem(item, response, xhr.status, headers);
            }
            this._onCompleteItem(item, response, xhr.status, headers);
        };
        xhr.onerror = () => {
            const headers = this._parseHeaders(xhr.getAllResponseHeaders());
            const response = this._transformResponse(xhr.response, headers);
            this._onErrorItem(item, response, xhr.status, headers);
            this._onCompleteItem(item, response, xhr.status, headers);
        };
        xhr.onabort = () => {
            const headers = this._parseHeaders(xhr.getAllResponseHeaders());
            const response = this._transformResponse(xhr.response, headers);
            this._onCancelItem(item, response, xhr.status, headers);
            this._onCompleteItem(item, response, xhr.status, headers);
        };
        if (item.method && item.url) {
            xhr.open(item.method, item.url, true);
        }
        xhr.withCredentials = item.withCredentials;
        if (this.options.headers) {
            for (const header of this.options.headers) {
                xhr.setRequestHeader(header.name, header.value);
            }
        }
        if (item.headers.length) {
            for (const header of item.headers) {
                xhr.setRequestHeader(header.name, header.value);
            }
        }
        if (this.authToken && this.authTokenHeader) {
            xhr.setRequestHeader(this.authTokenHeader, this.authToken);
        }
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                this.response.emit(xhr.responseText);
            }
        };
        if (this.options.formatDataFunctionIsAsync) {
            (sendable as unknown as Promise<FormData>).then((result: FormData) =>
                xhr.send(JSON.stringify(result))
            );
        } else {
            xhr.send(sendable);
        }
        this._render();
    }

    protected _getTotalProgress(value = 0): number {
        if (this.options.removeAfterUpload) {
            return value;
        }
        const notUploaded = this.getNotUploadedItems().length;
        const uploaded = notUploaded ? this.queue.length - notUploaded : this.queue.length;
        const ratio = 100 / this.queue.length;
        const current = (value * ratio) / 100;
        return Math.round(uploaded * ratio + current);
    }

    protected _getFilters(filters?: FilterFunction[] | string): FilterFunction[] {
        if (!filters) {
            return this.options?.filters || [];
        }
        if (Array.isArray(filters)) {
            return filters;
        }
        if (typeof filters === "string") {
            const names = filters.match(/[^\s,]+/g);

            return (this.options?.filters || []).filter(
                filter => names?.indexOf(filter.name) !== -1
            );
        }

        return this.options?.filters ?? [];
    }

    protected _render(): undefined {
        return undefined;
    }

    protected _queueLimitFilter(): boolean {
        return this.options.queueLimit === undefined || this.queue.length < this.options.queueLimit;
    }

    protected _isValidFile(
        file: FileLikeObject,
        filters: FilterFunction[],
        options: FileUploaderOptions
    ): boolean {
        this._failFilterIndex = -1;

        return !filters.length
            ? true
            : filters.every((filter: FilterFunction) => {
                  if (typeof this._failFilterIndex === "number") {
                      this._failFilterIndex++;
                  }
                  return filter.fn.call(this, file, options);
              });
    }

    protected _isSuccessCode(status: number): boolean {
        return (status >= 200 && status < 300) || status === 304;
    }

    protected _transformResponse(response: string, headers: ParsedResponseHeaders): string {
        return response;
    }

    protected _parseHeaders(headers: string): ParsedResponseHeaders {
        const parsed: ParsedResponseHeaders = {};
        let key: string;
        let val: string;
        let i: number;
        if (!headers) {
            return parsed;
        }
        headers.split("\n").forEach((line: string) => {
            i = line.indexOf(":");
            key = line.slice(0, i).trim().toLowerCase();
            val = line.slice(i + 1).trim();
            if (key) {
                parsed[key] = parsed[key] ? parsed[key] + ", " + val : val;
            }
        });

        return parsed;
    }

    protected _onWhenAddingFileFailed(
        item: FileLikeObject,
        filter: FilterFunction,
        options: unknown
    ): void {
        this.onWhenAddingFileFailed(item, filter, options);
    }

    protected _onAfterAddingFile(item: FileItem): void {
        this.onAfterAddingFile(item);
    }

    protected _onAfterAddingAll(items: FileItem[]): void {
        this.onAfterAddingAll(items);
    }

    protected _onBeforeUploadItem(item: FileItem): void {
        item._onBeforeUpload();
        this.onBeforeUploadItem(item);
    }

    protected _onBuildItemForm(item: FileItem, form: FormData): void {
        item._onBuildForm(form);
        this.onBuildItemForm(item, form);
    }

    protected _onProgressItem(item: FileItem, progress: number): void {
        const total = this._getTotalProgress(progress);
        this.progress = total;
        item._onProgress(progress);
        this.onProgressItem(item, progress);
        this.onProgressAll(total);
        this._render();
    }

    protected _onSuccessItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): void {
        item._onSuccess(response, status, headers);
        this.onSuccessItem(item, response, status, headers);
    }

    protected _onCancelItem(
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
    ): void {
        item._onCancel(response, status, headers);
        this.onCancelItem(item, response, status, headers);
    }
}
